import { type SyntheticEvent, type FC } from 'react';
import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { CheckedIcon } from '@wwe-portal/ui/components/icons/checked-icon';
import { TurnBackIcon } from '@wwe-portal/ui/components/icons/turn-back-icon';
import { UnCheckedIcon } from '@wwe-portal/ui/components/icons/un-checked-icon';
import { cn } from '@wwe-portal/ui/lib/lib';
import bronzePlanBanner from '@wwe-portal/ui/assets/images/subscription/bronze_plan_banner.webp';
import haloDimLight from '@wwe-portal/ui/assets/images/subscription/halo-dim-light.webp';
import silverPlanBanner from '@wwe-portal/ui/assets/images/subscription/silver_plan_banner.webp';
import type {
  BaseSubscriptionPlan,
  PlanBenefit,
} from '@wwe-portal/entities/subscription.type';
import { SubscriptionTier } from '@wwe-portal/entities/subscription.type';

import { SubcribeButton } from './subscribe-button';
import { Each } from '../../../components/utils';
import {
  FlipCard,
  FlipCardBack,
  FlipCardFront,
} from '@wwe-portal/ui/components/flip-card';

const banners: Record<SubscriptionTier, string> = {
  [SubscriptionTier.BRONZE]: bronzePlanBanner,
  [SubscriptionTier.SILVER]: silverPlanBanner,
  [SubscriptionTier.GOLD]: silverPlanBanner,
};

export interface SubscriptionCardProps {
  active: boolean;
  subscribing: boolean;
  isDisabled: boolean;
  showMoreInfo: boolean;
  plan: BaseSubscriptionPlan;
  onClickMoreInfo?: (e: SyntheticEvent) => void;
  onClickCard?: (e: SyntheticEvent) => void;
  onSubscribe?: (e: SyntheticEvent) => void;
}

const ContentItems: FC<{
  benefits: PlanBenefit[];
  listClassName?: string;
  sectionClassName?: string;
  subListClasName?: string;
}> = ({ benefits, listClassName, subListClasName, sectionClassName }) => (
  <ul
    className={cn(
      'mb-0 flex h-fit w-full select-none list-outside list-none flex-col justify-start gap-1 p-0',
      listClassName
    )}
  >
    <Each
      data={benefits}
      render={(item, index) => (
        <li
          key={index}
          className={cn(
            'flex flex-col gap-[2px] leading-[12px]',
            sectionClassName
          )}
        >
          <div className="text-text300 flex max-w-full items-baseline justify-start text-xs font-medium uppercase leading-[12px] text-white">
            {item.content}
            {item.featured ? (
              <CheckedIcon className="text-green600 ml-2 h-[10px] w-[13px]" />
            ) : (
              <UnCheckedIcon className="ml-2 h-[10px] w-[13px]" />
            )}
          </div>
          <ul
            className={cn(
              'flex list-outside flex-col gap-[2px] leading-[12px]',
              {
                'list-disc pl-4': item.subContent.length > 1,
                'list-none': item.subContent.length === 1,
              },
              subListClasName
            )}
          >
            <Each
              data={item.subContent}
              render={(subContent, index) => (
                <li
                  key={index}
                  className="font-poppins text-text400 text-2xs relative pl-0 font-normal leading-3 marker:text-[8px]"
                >
                  <span
                    className={cn('relative', {
                      '-left-[4px]': item.subContent.length > 1,
                    })}
                  >
                    {subContent}
                  </span>
                </li>
              )}
            />
          </ul>
        </li>
      )}
    />
  </ul>
);

const Header: FC<
  Pick<
    SubscriptionCardProps,
    'plan' | 'active' | 'showMoreInfo' | 'onClickCard'
  >
> = ({ plan: { displayName, tier }, active, showMoreInfo, onClickCard }) => {
  return (
    <ClickableDiv
      className={cn('h-[76px] w-full bg-cover bg-[50%_50%]', {
        'cursor-default': !showMoreInfo,
      })}
      onClick={(e) => {
        e.stopPropagation();
        onClickCard?.(e);
      }}
      role="none"
    >
      <div className="relative h-full w-full">
        <div className="flex translate-y-[-20px] flex-col items-center gap-1">
          <div className="relative">
            <img
              alt={displayName}
              className="top-[-2px] h-[72px] w-[100px]"
              src={banners[tier]}
            />
            <div className="absolute inset-x-0 -top-5 flex items-center justify-center">
              <img alt="Halo dim" className="hidden" src={haloDimLight} />
            </div>
          </div>
          <div className="relative flex h-[33px] w-[316px] justify-center leading-[29px]">
            <span className="tracking-norma text-green400 px-4 text-[22px] font-bold opacity-75 blur-[2px]">
              {displayName}
            </span>
            <span className="text-green800 absolute stroke-1 px-4 text-[22px] font-bold">
              {displayName}
            </span>
            <span className="absolute bg-gradient-to-b from-[#B4FFEA] from-50% to-[#0DD39C] to-70% bg-clip-text px-4 text-[22px] font-bold text-transparent drop-shadow-[2px_2px_4px_rgba(0,0,0,0.45)]">
              {displayName}
            </span>
          </div>
        </div>
        {active && !showMoreInfo ? (
          <div className="absolute right-3 top-3 flex flex-col items-center gap-1">
            <div className="flex h-6 w-[65px] select-none flex-row items-center justify-center rounded-md bg-[#FF144D] text-xs font-bold text-white">
              <span>ACTIVE</span>
            </div>
          </div>
        ) : null}
        {showMoreInfo && (
          <div className="absolute right-[18px] top-[14px]">
            <TurnBackIcon
              className="drop-shadow-[0_2px_2px_rgba(0,0,0,0.45)]"
              height={22}
              width={22}
            />
          </div>
        )}
      </div>
    </ClickableDiv>
  );
};

const FrontContent: FC<
  Pick<
    SubscriptionCardProps,
    'plan' | 'onClickMoreInfo' | 'showMoreInfo' | 'active'
  >
> = ({ plan: { benefits }, onClickMoreInfo, showMoreInfo }) => {
  return (
    <div className="flex h-full cursor-default flex-col items-center">
      <div className="h-[76px] w-full bg-cover bg-[50%_50%]">
        <div className="bg-back200 relative h-full w-full" />
      </div>
      <div className="flex h-0 w-full flex-grow flex-col items-center gap-5 pb-1 pt-4">
        <div className="relative h-full w-full overflow-auto pl-7 pr-4">
          <ContentItems benefits={benefits} />
          {showMoreInfo && (
            <ClickableDiv
              className="text-green400 font-poppins inline-block cursor-auto pt-2 text-left text-xs font-semibold leading-[22px] underline"
              onClick={onClickMoreInfo}
            >
              More Info
            </ClickableDiv>
          )}
        </div>
      </div>
    </div>
  );
};

const BackContent: FC<{
  moreInfoItems: PlanBenefit[];
}> = ({ moreInfoItems }) => {
  return (
    <div className="flex h-full flex-col items-center">
      <div className="h-[76px] w-full bg-cover bg-[50%_50%]">
        <div className="bg-back200 relative h-full w-full" />
      </div>
      <div className="relative w-full overflow-auto pl-7 pr-5 pt-[40px]">
        <ContentItems
          benefits={moreInfoItems}
          listClassName="gap-[6px]"
          sectionClassName="gap-1"
          subListClasName="gap-1"
        />
        <div className="text-2xs pl-3 pt-3 font-normal leading-[10px] text-white">
          <i>
            Note: Existing IN-GAME VIP members will keep their current benefits
            without any changes.
          </i>
        </div>
        <div className="text-2xs pl-3 pt-8 font-normal leading-[10px] text-white">
          <i>For more details, see the FAQs section.</i>
        </div>
      </div>
    </div>
  );
};

const Layout: FC<
  Pick<
    SubscriptionCardProps,
    | 'plan'
    | 'active'
    | 'onSubscribe'
    | 'onClickCard'
    | 'onClickMoreInfo'
    | 'isDisabled'
    | 'subscribing'
  > & {
    content: React.ReactNode;
    header: React.ReactNode;
  }
> = ({
  plan,
  content,
  header,
  active,
  onSubscribe,
  onClickCard,
  isDisabled,
  subscribing,
}) => {
  return (
    <div
      className={cn(
        'font-poppins relative h-[482px] select-none drop-shadow-[1px_1px_10px_#AEE9FF]'
      )}
    >
      <ClickableDiv
        className={cn(
          'bg-back600 absolute inset-0 isolate flex flex-col overflow-hidden rounded-[10px] border-[3px] border-solid border-[#0085FF] bg-[#0D0F1C]',
          {
            'border-blue300': plan.tier === SubscriptionTier.BRONZE,
            'border-yellow500': plan.tier === SubscriptionTier.SILVER,
            'border-goldtier': plan.tier === SubscriptionTier.GOLD,
          }
        )}
        onClick={onClickCard}
        role="none"
      >
        <div className="h-0 flex-1 flex-grow">
          <div className="h-full">{content}</div>
        </div>
        <div className="w-full px-3 pb-2">
          <SubcribeButton
            active={active}
            isDisabled={isDisabled}
            onClick={(event) => {
              event.stopPropagation();
              onSubscribe?.(event);
            }}
            subscribing={subscribing}
          >
            {plan.price}
          </SubcribeButton>
        </div>
      </ClickableDiv>
      <div className="absolute inset-x-0 top-0">{header}</div>
    </div>
  );
};

export const SubscriptionCard: FC<SubscriptionCardProps> = (props) => {
  return (
    <FlipCard className="h-[482px]" flip={props.showMoreInfo}>
      <FlipCardFront>
        <Layout
          content={
            <FrontContent
              active={props.active}
              onClickMoreInfo={props.onClickMoreInfo}
              plan={props.plan}
              showMoreInfo
            />
          }
          header={
            <Header
              active={props.active}
              plan={props.plan}
              showMoreInfo={props.showMoreInfo}
            />
          }
          {...props}
        />
      </FlipCardFront>
      <FlipCardBack>
        <Layout
          content={<BackContent moreInfoItems={props.plan.moreInfoItems} />}
          header={
            <Header
              active={props.active}
              onClickCard={props.onClickCard}
              plan={props.plan}
              showMoreInfo={props.showMoreInfo}
            />
          }
          {...props}
        />
      </FlipCardBack>
    </FlipCard>
  );
};
